import React, { useMemo, useRef } from "react";

import { useAuth } from "@/common/hooks/useAuth";
import {
  useModuleTypes,
  useCreateModuleType,
  ModuleType,
} from "@/common/services/module-types";
import { MultiSearchableSelect } from "@/components/ui/multi-searchable-select";

interface ModuleTypeSelectProps {
  projectId?: number | string | null;
  value?: number | string | null;
  onChange: (id: number | null, name: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function ModuleTypeSelect({
  projectId,
  value,
  onChange,
  placeholder = "Select module type...",
  className,
  disabled = false,
}: ModuleTypeSelectProps) {
  const { userPreferenceData } = useAuth();
  const effectiveProjectId =
    projectId ||
    userPreferenceData?.data?.projectId ||
    (userPreferenceData?.data as any)?.project_id ||
    null;

  const { data: moduleTypes = [] } = useModuleTypes(effectiveProjectId);
  const createMutation = useCreateModuleType();
  // Prevent duplicate create calls
  const creatingRef = useRef<string | null>(null);

  const allOptions = useMemo(() => {
    return Array.isArray(moduleTypes) ? moduleTypes : [];
  }, [moduleTypes]);

  // Convert single value to array for MultiSearchableSelect
  const selectedArray = useMemo(() => {
    if (!value) return [];
    const found = allOptions.find((m) => String(m.id) === String(value));
    if (found) return [found];
    return [];
  }, [value, allOptions]);

  const handleChange = async (selected: ModuleType[]) => {
    if (selected.length === 0) {
      onChange(null, null);
      return;
    }
    // Only take the last item (single-select behaviour)
    const item = selected[selected.length - 1];
    const idStr = String(item.id);
    const numId = Number(item.id);

    // If the id is numeric and real, just call onChange directly
    if (!isNaN(numId) && numId > 0) {
      onChange(numId, item.name);
      return;
    }

    // Temp item created by allowCreate — id is "name_timestamp"
    // We need to create it in the backend first
    const name = item.name?.trim();
    if (!name) return;

    // Avoid duplicate API calls for the same name
    if (creatingRef.current === name) return;
    creatingRef.current = name;

    try {
      const pid = effectiveProjectId;
      if (!pid) {
        // No project id, just pass null back
        onChange(null, null);
        return;
      }
      const created = await createMutation.mutateAsync({
        projectId: pid,
        name,
      });
      // created should be the new ModuleType with a real id
      const realId = Number(created?.id);
      onChange(isNaN(realId) ? null : realId, created?.name ?? name);
    } catch (err) {
      console.error("Failed to create module type:", err);
      onChange(null, null);
    } finally {
      creatingRef.current = null;
    }
  };

  return (
    <MultiSearchableSelect<ModuleType>
      options={allOptions}
      labelKey="name"
      valueKey="id"
      selected={selectedArray}
      onChange={handleChange}
      placeholder={placeholder}
      searchable={true}
      allowCreate={true}
      disabled={disabled || createMutation.isPending}
      className={className}
    />
  );
}
